import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import { IFileData } from "csi/typings";
import { getFileNameWithoutExtension } from "csi/utility/fileUtility";
import React, { Fragment, FunctionComponent } from "react";
import Box from "../box/Box";
import DownloadButton from "../button/DownloadButton";
import IconButton from "../button/IconButton";
import { red } from "designSystem/styles/color";
import { preventDefaultBehaviour } from "designSystem/utilities/prevent-default-behavior";
import FlexRowCenter, { FlexRowCenterProps } from "../flex/FlexRowCenter";
import CsiImage from "../image/CsiImage";
import Link from "../link/Link";
import OverflowText from "../text/OverflowText";
import Text from "../text/Text";
import { TooltipProps } from "../tooltip/Tooltip";

const faIconMap = {
	doc: "far fa-file-word",
	docx: "far fa-file-word",
	pdf: "far fa-file-pdf",
	xls: "far fa-file-excel",
	xlsx: "far fa-file-excel",
};

export interface FileProps extends Omit<FlexRowCenterProps, "children"> {
	/** indicate if download action is available */
	download?: boolean;

	/** file data */
	fileData: IFileData;

	/** control whether the file name grow or not, true by default */
	fileNameGrow?: boolean;

	/** indicate if component can have hover background, which will change styling of buttons */
	hoverStyling?: boolean;

	/** callback for clicking on image link; if none provided, then link for image will not be shown */
	onImage?: () => void;

	/** callback for delete action; if none provided, then delete action will not be shown */
	onDelete?: () => void;

	/** callback for download action */
	onDownload?: () => void;

	/** indicate if file is currently being uploaded */
	uploading?: boolean;
}

const File: FunctionComponent<FileProps> = (props) => {
	const { download, fileData, hoverStyling, onDelete, onImage, onDownload, uploading, fileNameGrow, ...otherProps } = props;
	const { ext, name, thumbnail, url } = fileData;
	const faIcon = faIconMap[ext] || "far fa-file";
	const baseName = getFileNameWithoutExtension(name, true);

	// files will normally appear in list, so don't use default of "bottom"; we may need to expose a property
	// if this doesn't work in all cases
	const tooltipProps: Partial<TooltipProps> = {
		placement: "right",
		title: name,
	};

	const onClickDownload = (event) => {
		if (onDownload) {
			onDownload();
			preventDefaultBehaviour(event);
		}
	};

	return (
		<FlexRowCenter {...otherProps} onClick={preventDefaultBehaviour}>
			<Box
				centerContent
				flexShrink={0}
				height={20}
				mr={1}
				width={20}
			>
				{thumbnail ?
					<CsiImage borderRadius={3} src={thumbnail} />
					:
					<Icon className={faIcon} />
				}
			</Box>
			{thumbnail && onImage && !uploading ? (
				<Link grow onClick={onImage}>
					<OverflowText text={baseName} tooltipProps={tooltipProps} />
					<Text grow={fileNameGrow ?? true} text={ext} />
				</Link>
			)
				: (
					<Fragment>
						<OverflowText text={baseName} tooltipProps={tooltipProps} />
						<Text grow={fileNameGrow ?? true} text={ext} />
					</Fragment>
				)}
			{(download || onDelete || uploading) && (
				<FlexRowCenter height={1} ml={2}>
					{uploading ?
						<CircularProgress size={20} />
						: (
							<Fragment>
								{download && (
									<Fragment>
										{!onDelete && (
											<Box
												bgcolor="grey.600"
												height={12}
												mx={1}
												width="1px"
											/>
										)}
										<DownloadButton
											design={hoverStyling ? "defaultOnGrey" : "default"}
											href={url}
											onClick={onClickDownload}
											size="small"
										/>
									</Fragment>
								)}
								{onDelete && (
									<Fragment>
										<Box
											bgcolor="grey.600"
											height={12}
											mx={1}
											width="1px"
										/>
										<IconButton
											design={hoverStyling ? "defaultOnGrey" : "default"}
											onClick={onDelete}
											res="Delete"
											size="small"
											style={{ color: red[600] }}
										>
											<CloseIcon />
										</IconButton>
									</Fragment>
								)}
							</Fragment>
						)}
				</FlexRowCenter>
			)}
		</FlexRowCenter>
	);
};

export default File;